import { useQuery } from '@tanstack/react-query';
import { apiClient } from '@/lib/api-client';
import { useAuthStore } from '@/features/auth/hooks/useAuth';
import type { ApiResponse } from '@/lib/api-client';

/**
 * User permissions response from the API.
 */
interface UserPermissions {
  role: string;
  dataScope: 'assigned' | 'team' | 'all';
  permissions: Record<string, boolean>;
}

/**
 * Permission categories matching the backend constants.
 */
export const PERMISSION_CATEGORIES = {
  CONTACT_MANAGEMENT: 'contact_management',
  CLIENT_MANAGEMENT: 'client_management',
  GROUP_MANAGEMENT: 'group_management',
  JOB_MANAGEMENT: 'job_management',
  BILLING: 'billing',
  REPORTING: 'reporting',
  DOCUMENTS: 'documents',
  SETTINGS: 'settings',
  USER_ADMIN: 'user_admin',
} as const;

/**
 * Common permission strings for quick reference.
 */
export const PERMISSIONS = {
  // Dashboard
  DASHBOARD_VIEW: 'dashboard.view',
  DASHBOARD_VIEW_METRICS: 'dashboard.view_metrics',
  DASHBOARD_VIEW_REVENUE: 'dashboard.view_revenue',
  DASHBOARD_VIEW_JOB_STATUS: 'dashboard.view_job_status',
  DASHBOARD_VIEW_STAFF_UTILIZATION: 'dashboard.view_staff_utilization',
  DASHBOARD_VIEW_MY_REPORTS: 'dashboard.view_my_reports',

  // Contact management
  CONTACTS_READ: 'contact_management.read',
  CONTACTS_CREATE: 'contact_management.create',
  CONTACTS_UPDATE: 'contact_management.update',
  CONTACTS_DELETE: 'contact_management.delete',
  CONTACTS_IMPORT: 'contact_management.import',
  CONTACTS_EXPORT: 'contact_management.export',
  CONTACTS_BULK_ASSIGN: 'contact_management.bulk_assign',
  CONTACTS_MANAGE_NOTIFICATIONS: 'contact_management.manage_notifications',

  // Group & relationship management
  GROUPS_READ: 'group_management.read',
  GROUPS_CREATE: 'group_management.create',
  GROUPS_UPDATE: 'group_management.update',
  GROUPS_DELETE: 'group_management.delete',
  GROUPS_MANAGE_RELATIONSHIPS: 'group_management.manage_relationships',
  GROUPS_MANAGE_INVOICING: 'group_management.manage_invoicing',

  // Client management
  CLIENTS_READ: 'client_management.read',
  CLIENTS_CREATE: 'client_management.create',
  CLIENTS_UPDATE: 'client_management.update',
  CLIENTS_DELETE: 'client_management.delete',
  CLIENTS_CONVERT: 'client_management.convert',
  CLIENTS_MANAGE_PORTAL: 'client_management.manage_portal',

  // Job management
  JOBS_READ: 'job_management.read',
  JOBS_CREATE: 'job_management.create',
  JOBS_UPDATE: 'job_management.update',
  JOBS_DELETE: 'job_management.delete',
  JOBS_ASSIGN: 'job_management.assign',
  JOBS_TRANSITION: 'job_management.transition',
  JOBS_CHANGE_WORKFLOW: 'job_management.change_workflow',
  JOBS_CHANGE_STATUS: 'job_management.change_status',
  JOBS_ASSIGN_TASK_TO_CLIENT: 'job_management.assign_task_to_client',
  JOBS_MANAGE_WORKFLOWS: 'job_management.manage_workflows',
  JOBS_BULK_CREATE: 'job_management.bulk_create',
  JOBS_CREATE_TASK: 'job_management.create_task',
  JOBS_MANAGE_RECURRENCE: 'job_management.manage_recurrence',

  // Billing
  BILLING_READ: 'billing.read',
  BILLING_CREATE_INVOICE: 'billing.create_invoice',
  BILLING_UPDATE_INVOICE: 'billing.update_invoice',
  BILLING_SEND_INVOICE: 'billing.send_invoice',
  BILLING_VOID_INVOICE: 'billing.void_invoice',
  BILLING_MARK_BAD_DEBT: 'billing.mark_bad_debt',
  BILLING_RECORD_PAYMENT: 'billing.record_payment',
  BILLING_CREATE_CREDIT_NOTE: 'billing.create_credit_note',
  BILLING_CREATE_WRITE_OFF: 'billing.create_write_off',
  BILLING_CREATE_DISCOUNT: 'billing.create_discount',
  BILLING_CREATE_DEBIT_NOTE: 'billing.create_debit_note',
  BILLING_CREATE_ADVANCE: 'billing.create_advance',
  BILLING_MANAGE_QUOTES: 'billing.manage_quotes',
  BILLING_MANAGE_RECURRING: 'billing.manage_recurring',
  BILLING_BULK_GENERATE: 'billing.bulk_generate',
  BILLING_VIEW_LEDGER: 'billing.view_ledger',
  BILLING_CLOSE_PERIOD: 'billing.close_period',
  BILLING_EXPORT_LEDGER: 'billing.export_ledger',
  BILLING_MANAGE_PERIODS: 'billing.manage_periods',
  BILLING_DELETE_PERIOD: 'billing.delete_period',

  // Reporting
  REPORTING_READ: 'reporting.read',
  REPORTING_GENERATE: 'reporting.generate_reports',
  REPORTING_EXPORT: 'reporting.export_reports',
  REPORTING_SCHEDULE: 'reporting.schedule_reports',
  REPORTING_VIEW_FINANCIALS: 'reporting.view_financials',
  // Per-module report data-source access
  REPORTING_SOURCE_CONTACTS: 'reporting.contacts',
  REPORTING_SOURCE_JOBS: 'reporting.jobs',
  REPORTING_SOURCE_BILLING: 'reporting.billing',
  REPORTING_SOURCE_TIME_ENTRIES: 'reporting.time_entries',
  REPORTING_SOURCE_WIP: 'reporting.wip',
  REPORTING_SOURCE_CLIENT_LEDGER: 'reporting.client_ledger',
  REPORTING_SOURCE_CAPACITY: 'reporting.capacity',
  REPORTING_SOURCE_DOCUMENTS: 'reporting.documents',
  REPORTING_SOURCE_USERS: 'reporting.users',
  REPORTING_SOURCE_AUDIT_LOGS: 'reporting.audit_logs',

  // Documents
  DOCUMENTS_READ: 'documents.read',
  DOCUMENTS_UPLOAD: 'documents.upload',
  DOCUMENTS_DOWNLOAD: 'documents.download',
  DOCUMENTS_DELETE: 'documents.delete',
  DOCUMENTS_MANAGE_FOLDERS: 'documents.manage_folders',
  DOCUMENTS_CREATE_REQUESTS: 'documents.create_requests',
  DOCUMENTS_VIEW_ALL: 'documents.view_all',

  // Notifications
  NOTIFICATIONS_READ: 'notifications.read',
  NOTIFICATIONS_UPDATE: 'notifications.update',

  // Form Builder
  FORM_BUILDER_READ: 'form_builder.read',
  FORM_BUILDER_CREATE: 'form_builder.create',
  FORM_BUILDER_UPDATE: 'form_builder.update',
  FORM_BUILDER_DELETE: 'form_builder.delete',

  // Branding
  BRANDING_READ: 'branding.read',
  BRANDING_UPDATE: 'branding.update',

  // Document Templates
  DOCUMENT_TEMPLATES_READ: 'document_templates.read',
  DOCUMENT_TEMPLATES_CREATE: 'document_templates.create',
  DOCUMENT_TEMPLATES_UPDATE: 'document_templates.update',
  DOCUMENT_TEMPLATES_DELETE: 'document_templates.delete',
  DOCUMENT_TEMPLATES_GENERATE: 'document_templates.generate',

  // Settings
  SETTINGS_VIEW: 'settings.view',
  SETTINGS_UPDATE_GENERAL: 'settings.update_general',
  SETTINGS_MANAGE_WORKFLOWS: 'settings.manage_workflows',
  SETTINGS_MANAGE_TEMPLATES: 'settings.manage_templates',
  SETTINGS_MANAGE_DYNAMIC_FIELDS: 'settings.manage_dynamic_fields',
  SETTINGS_MANAGE_SMTP: 'settings.manage_smtp',
  SETTINGS_MANAGE_BILLING: 'settings.manage_billing_settings',
  SETTINGS_MANAGE_PORTAL: 'settings.manage_portal_settings',

  // User admin
  USER_ADMIN_READ: 'user_admin.read',
  USER_ADMIN_CREATE: 'user_admin.create',
  USER_ADMIN_UPDATE: 'user_admin.update',
  USER_ADMIN_DEACTIVATE: 'user_admin.deactivate',
  USER_ADMIN_MANAGE_ROLES: 'user_admin.manage_roles',
  USER_ADMIN_VIEW_AUDIT_LOG: 'user_admin.view_audit_log',
  USER_ADMIN_EXPORT_AUDIT_LOG: 'user_admin.export_audit_log',

  // Note categories
  NOTE_CATEGORIES_READ: 'note_categories.read',
  NOTE_CATEGORIES_CREATE: 'note_categories.create',
  NOTE_CATEGORIES_UPDATE: 'note_categories.update',
  NOTE_CATEGORIES_DELETE: 'note_categories.delete',
  NOTE_CATEGORIES_MANAGE: 'note_categories.manage',

  // Data migration
  MIGRATION_READ: 'migration.read',
  MIGRATION_WRITE: 'migration.write',

  // Data authorization
  DATA_AUTHORIZATION_READ: 'data_authorization.read',
  DATA_AUTHORIZATION_MANAGE: 'data_authorization.manage',

  // Data scope override
  DATA_SCOPE_VIEW_ALL: 'data_scope.view_all',

  // Time entries
  TIME_ENTRIES_READ: 'time_entries.read',

  // WIP (Work In Progress)
  WIP_READ: 'wip.read',
  WIP_CREATE: 'wip.create',
  WIP_UPDATE: 'wip.update',
  WIP_DELETE: 'wip.delete',

  // Capacity & workload
  CAPACITY_READ: 'capacity.read',
  CAPACITY_MANAGE: 'capacity.manage',
} as const;

/**
 * Hook to fetch and check user permissions for RBAC-based UI rendering.
 *
 * Usage:
 * ```tsx
 * const { can, canAny, canAll, dataScope, isLoading } = usePermissions();
 *
 * // Check single permission
 * if (can(PERMISSIONS.JOBS_CREATE)) { ... }
 *
 * // Check any of multiple permissions
 * if (canAny([PERMISSIONS.BILLING_READ, PERMISSIONS.REPORTING_VIEW_FINANCIALS])) { ... }
 *
 * // Conditionally render UI
 * {can(PERMISSIONS.CONTACTS_CREATE) && <CreateContactButton />}
 * ```
 */
export function usePermissions() {
  const { isAuthenticated, user } = useAuthStore();

  const { data: permissions, isLoading } = useQuery({
    queryKey: ['user-permissions', user?.id],
    queryFn: async () => {
      const response = await apiClient.get<ApiResponse<UserPermissions>>('/roles/permissions/me');
      return response.data.data;
    },
    enabled: isAuthenticated && !!user,
    staleTime: 30 * 1000, // 30 seconds — permissions can change quickly (Req 3.4)
    gcTime: 60 * 1000,
  });

  /**
   * Check if the user has a specific permission.
   */
  function can(permission: string): boolean {
    if (!permissions) return false;
    return permissions.permissions[permission] === true;
  }

  /**
   * Check if the user has ANY of the specified permissions.
   */
  function canAny(permissionList: string[]): boolean {
    if (!permissions) return false;
    return permissionList.some((p) => permissions.permissions[p] === true);
  }

  /**
   * Check if the user has ALL of the specified permissions.
   */
  function canAll(permissionList: string[]): boolean {
    if (!permissions) return false;
    return permissionList.every((p) => permissions.permissions[p] === true);
  }

  /**
   * Check if the user has any permission in a given category.
   */
  function canAccessCategory(category: string): boolean {
    if (!permissions) return false;
    return Object.entries(permissions.permissions).some(
      ([key, value]) => key.startsWith(`${category}.`) && value === true,
    );
  }

  /**
   * Get the user's data scope level.
   * Controls which records the user can see (assigned, team, all).
   */
  const dataScope = permissions?.dataScope ?? 'assigned';

  /**
   * The user's role name.
   */
  const role = permissions?.role ?? user?.role ?? '';

  /**
   * Whether the user is an Administrator (full access).
   * Checks both the authorization role name and the user's job role.
   */
  const jobRole = user?.jobRole ?? '';
  const isAdmin =
    role.toLowerCase() === 'administrator' ||
    role.toLowerCase() === 'admin' ||
    jobRole.toLowerCase() === 'administrator' ||
    jobRole.toLowerCase() === 'admin';

  return {
    can,
    canAny,
    canAll,
    canAccessCategory,
    dataScope,
    role,
    isAdmin,
    isLoading,
    permissions: permissions?.permissions ?? {},
  };
}
